/**
 * Hub Store - Inbox unificata multi-canale
 *
 * Sotto-moduli:
 * - Conversazioni (cnv-)
 * - Messaggi (msg-)
 * - Contatti (cnt-)
 *
 * Canali supportati: email, whatsapp, telegram, call, videocall, sms
 * Usa Repository Pattern per database-agnostic persistence.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { genId } from '../domain/idUtils';
import { createZustandStorage } from '../storage/adapter';
import { getRepository } from '../repository';
import type { IDataRepository } from '../repository';
import { graphqlHubRepository } from '../repositories/GraphQLHubRepository';
import type {
  Conversation,
  MessageItem,
  Contact,
  ContactHandles,
  Channel,
  Direction,
  SidebarMenu,
} from '../domain/models';

// =============================================================================
// SIDEBAR MENU CONFIG
// =============================================================================

const HUB_SIDEBAR_MENU: SidebarMenu = {
  sections: [
    {
      title: 'Inbox',
      items: [
        { key: 'all', label: 'Tutti', hint: 'Tutti i messaggi' },
        { key: 'unread', label: 'Non letti', hint: 'Da leggere' },
        { key: 'recent', label: 'Recenti', hint: 'Ultime 24h' },
      ],
    },
    {
      title: 'Canali',
      items: [
        { key: 'email', label: 'Email', icon: 'mail' },
        { key: 'whatsapp', label: 'WhatsApp', icon: 'message-circle' },
        { key: 'telegram', label: 'Telegram', icon: 'send' },
        { key: 'call', label: 'Chiamate', icon: 'phone' },
        { key: 'videocall', label: 'Videochiamate', icon: 'video' },
      ],
    },
    {
      title: 'Collegamenti',
      items: [
        { key: 'with-project', label: 'Con progetto' },
        { key: 'without-project', label: 'Senza progetto' },
      ],
    },
  ],
};

// =============================================================================
// REPOSITORY INSTANCES
// =============================================================================

let conversationsRepo: IDataRepository;
let messagesRepo: IDataRepository;
let contactsRepo: IDataRepository;

// Initialize repositories
async function initRepositories() {
  conversationsRepo = await getRepository();
  messagesRepo = await getRepository();
  contactsRepo = await getRepository();
}

// Auto-initialize on module load
initRepositories().catch(console.error);

// =============================================================================
// STORE STATE
// =============================================================================

interface HubState {
  // View state
  activeFilterKey: string;
  selectedConversationId: string | null;
  selectedContactId: string | null;

  // Data (in-memory cache, source of truth is repository)
  conversations: Conversation[];
  messages: MessageItem[];
  contacts: Contact[];

  // UI
  sidebarMenu: SidebarMenu;
  status: 'idle' | 'loading' | 'error';
  error: string | null;
  initialized: boolean;

  // Actions - Initialization
  loadData: () => Promise<void>;
  loadFromServer: () => Promise<void>;

  // Actions - View
  setActiveFilterKey: (key: string) => void;
  selectConversation: (id: string | null) => void;
  selectContact: (id: string | null) => void;

  // Actions - Conversations (async)
  createConversation: (data: Omit<Conversation, 'id' | 'lastAt'>) => Promise<string>;
  updateConversation: (id: string, data: Partial<Conversation>) => Promise<boolean>;
  deleteConversation: (id: string) => Promise<boolean>;
  getConversation: (id: string) => Conversation | null;
  markAsRead: (conversationId: string) => Promise<void>;

  // Actions - Messages (async)
  addMessage: (conversationId: string, data: Omit<MessageItem, 'id' | 'conversationId' | 'at'>) => Promise<string>;
  getConversationMessages: (conversationId: string) => MessageItem[];

  // Actions - Contacts (async)
  createContact: (data: Omit<Contact, 'id' | 'createdAt' | 'updatedAt'>) => Promise<string>;
  updateContact: (id: string, data: Partial<Contact>) => Promise<boolean>;
  deleteContact: (id: string) => Promise<boolean>;
  getContact: (id: string) => Contact | null;
  findContactByHandle: (handle: string) => Contact | null;

  // Selectors
  getFilteredConversations: () => Conversation[];
  getConversationsByChannel: (channel: Channel) => Conversation[];
  getUnreadConversations: () => Conversation[];
  getRecentConversations: (hours?: number) => Conversation[];
  getConversationContact: (conversationId: string) => Contact | null;
}

// =============================================================================
// STORE
// =============================================================================

export const useHubStore = create<HubState>()(
  persist(
    (set, get) => ({
      // Initial state
      activeFilterKey: 'all',
      selectedConversationId: null,
      selectedContactId: null,
      conversations: [],
      messages: [],
      contacts: [],
      sidebarMenu: HUB_SIDEBAR_MENU,
      status: 'idle',
      error: null,
      initialized: false,

      // Load data from repository
      loadData: async () => {
        try {
          set({ status: 'loading' });

          const [conversations, messages, contacts] = await Promise.all([
            conversationsRepo.query<Conversation>({ scope: 'conversations' }),
            messagesRepo.query<MessageItem>({ scope: 'messages' }),
            contactsRepo.query<Contact>({ scope: 'contacts' }),
          ]);

          set({
            conversations,
            messages,
            contacts,
            status: 'idle',
            initialized: true,
          });
        } catch (error) {
          console.error('Failed to load hub data:', error);
          set({ status: 'error', error: String(error) });
        }
      },

      // Load data from GraphQL server
      loadFromServer: async () => {
        const state = get();
        if (state.initialized || state.status === 'loading') return;

        try {
          set({ status: 'loading', error: null });

          // Load all data from GraphQL in parallel
          const [serverContacts, serverConversations] = await Promise.all([
            graphqlHubRepository.listContacts(),
            graphqlHubRepository.listConversations(),
          ]);

          // Map server contacts to local Contact type
          const contacts: Contact[] = serverContacts.map(c => ({
            id: c.id,
            displayName: c.displayName,
            handles: {},
            company: c.company,
            role: c.role,
            notes: c.notes,
            tags: c.tags || [],
            createdAt: c.createdAt,
            updatedAt: c.updatedAt,
          }));

          // Map server conversations to local Conversation type
          const conversations: Conversation[] = serverConversations.map(c => ({
            id: c.id,
            channel: (c.channel || 'email') as Channel,
            title: c.title,
            lastAt: c.lastMessageAt || c.updatedAt,
            contactId: c.contactId,
            contactName: c.contactName,
            unread: c.unreadCount,
            projectId: c.projectId,
          }));

          // Load messages for each conversation
          const allMessages: MessageItem[] = [];
          for (const conv of serverConversations) {
            const serverMessages = await graphqlHubRepository.listMessages(conv.id);
            const messages: MessageItem[] = serverMessages.map(m => ({
              id: m.id,
              conversationId: m.conversationId,
              at: m.timestamp,
              text: m.content,
              direction: (m.direction || 'out') as Direction,
              read: m.isRead,
            }));
            allMessages.push(...messages);
          }

          set({
            contacts,
            conversations,
            messages: allMessages,
            status: 'idle',
            initialized: true,
          });
        } catch (error) {
          console.error('Failed to load hub data from server:', error);
          set({
            status: 'error',
            error: error instanceof Error ? error.message : 'Failed to load data from server'
          });
        }
      },

      // View actions
      setActiveFilterKey: (key) => set({ activeFilterKey: key }),
      selectConversation: (id) => set({ selectedConversationId: id }),
      selectContact: (id) => set({ selectedContactId: id }),

      // Conversations (async)
      createConversation: async (data) => {
        const id = genId('cnv');
        const conversation: Conversation = {
          ...data,
          id,
          lastAt: new Date().toISOString(),
        };

        await conversationsRepo.put(conversation);

        set((state) => ({
          conversations: [conversation, ...state.conversations],
        }));

        return id;
      },

      updateConversation: async (id, data) => {
        const state = get();
        const conversation = state.conversations.find((c) => c.id === id);
        if (!conversation) return false;

        const updated = { ...conversation, ...data };
        await conversationsRepo.put(updated);

        set((state) => ({
          conversations: state.conversations.map((c) => (c.id === id ? updated : c)),
        }));

        return true;
      },

      deleteConversation: async (id) => {
        const state = get();
        if (!state.conversations.some((c) => c.id === id)) return false;

        await conversationsRepo.delete(id);

        const relatedMessages = state.messages.filter((m) => m.conversationId === id);
        await Promise.all(relatedMessages.map((m) => messagesRepo.delete(m.id)));

        set((state) => ({
          conversations: state.conversations.filter((c) => c.id !== id),
          messages: state.messages.filter((m) => m.conversationId !== id),
          selectedConversationId: state.selectedConversationId === id ? null : state.selectedConversationId,
        }));

        return true;
      },

      getConversation: (id) => get().conversations.find((c) => c.id === id) || null,

      markAsRead: async (conversationId) => {
        const state = get();
        const conversation = state.conversations.find((c) => c.id === conversationId);
        if (conversation) {
          const updated = { ...conversation, unread: 0 };
          await conversationsRepo.put(updated);
        }

        const messagesToUpdate = state.messages.filter((m) => m.conversationId === conversationId && !m.read);
        await Promise.all(
          messagesToUpdate.map((m) => messagesRepo.put({ ...m, read: true }))
        );

        set((state) => ({
          conversations: state.conversations.map((c) =>
            c.id === conversationId ? { ...c, unread: 0 } : c
          ),
          messages: state.messages.map((m) =>
            m.conversationId === conversationId ? { ...m, read: true } : m
          ),
        }));
      },

      // Messages (async)
      addMessage: async (conversationId, data) => {
        const id = genId('msg');
        const message: MessageItem = {
          ...data,
          id,
          conversationId,
          at: new Date().toISOString(),
        };

        await messagesRepo.put(message);

        // Update conversation
        const state = get();
        const conversation = state.conversations.find((c) => c.id === conversationId);
        if (conversation) {
          const updated = {
            ...conversation,
            lastAt: message.at,
            unread: data.direction === 'in' ? (conversation.unread || 0) + 1 : conversation.unread,
          };
          await conversationsRepo.put(updated);
        }

        set((state) => ({
          messages: [...state.messages, message],
          conversations: state.conversations.map((c) =>
            c.id === conversationId
              ? {
                  ...c,
                  lastAt: message.at,
                  unread: data.direction === 'in' ? (c.unread || 0) + 1 : c.unread,
                }
              : c
          ),
        }));

        return id;
      },

      getConversationMessages: (conversationId) => {
        return get().messages
          .filter((m) => m.conversationId === conversationId)
          .sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
      },

      // Contacts (async)
      createContact: async (data) => {
        const now = new Date().toISOString();
        const id = genId('cnt');
        const contact: Contact = {
          ...data,
          id,
          createdAt: now,
          updatedAt: now,
        };

        await contactsRepo.put(contact);

        set((state) => ({
          contacts: [contact, ...state.contacts],
        }));

        return id;
      },

      updateContact: async (id, data) => {
        const state = get();
        const contact = state.contacts.find((c) => c.id === id);
        if (!contact) return false;

        const updated = { ...contact, ...data, updatedAt: new Date().toISOString() };
        await contactsRepo.put(updated);

        set((state) => ({
          contacts: state.contacts.map((c) => (c.id === id ? updated : c)),
        }));

        return true;
      },

      deleteContact: async (id) => {
        const state = get();
        if (!state.contacts.some((c) => c.id === id)) return false;

        await contactsRepo.delete(id);

        set((state) => ({
          contacts: state.contacts.filter((c) => c.id !== id),
          selectedContactId: state.selectedContactId === id ? null : state.selectedContactId,
        }));

        return true;
      },

      getContact: (id) => get().contacts.find((c) => c.id === id) || null,

      findContactByHandle: (handle) => {
        const normalizedHandle = handle.toLowerCase();
        return get().contacts.find((c) => {
          const handles = c.handles;
          return (
            handles.email?.toLowerCase() === normalizedHandle ||
            handles.phone === handle ||
            handles.whatsapp === handle ||
            handles.telegram?.toLowerCase() === normalizedHandle
          );
        }) || null;
      },

      // Selectors
      getFilteredConversations: () => {
        const state = get();
        const key = state.activeFilterKey;

        switch (key) {
          case 'unread':
            return state.conversations.filter((c) => (c.unread || 0) > 0);
          case 'recent':
            const dayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
            return state.conversations.filter((c) => c.lastAt >= dayAgo);
          case 'email':
          case 'whatsapp':
          case 'telegram':
          case 'call':
          case 'videocall':
          case 'sms':
            return state.conversations.filter((c) => c.channel === key);
          case 'with-project':
            return state.conversations.filter((c) => c.projectId);
          case 'without-project':
            return state.conversations.filter((c) => !c.projectId);
          default:
            return state.conversations;
        }
      },

      getConversationsByChannel: (channel) => {
        return get().conversations.filter((c) => c.channel === channel);
      },

      getUnreadConversations: () => {
        return get().conversations.filter((c) => (c.unread || 0) > 0);
      },

      getRecentConversations: (hours = 24) => {
        const cutoff = new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
        return get().conversations.filter((c) => c.lastAt >= cutoff);
      },

      getConversationContact: (conversationId) => {
        const conversation = get().conversations.find((c) => c.id === conversationId);
        if (!conversation?.contactId) return null;
        return get().contacts.find((c) => c.id === conversation.contactId) || null;
      },
    }),
    {
      name: 'hub-store',
      storage: createZustandStorage(),
      // Only persist UI state, not data (data goes to repository)
      partialize: (state) => ({
        activeFilterKey: state.activeFilterKey,
        selectedConversationId: state.selectedConversationId,
        selectedContactId: state.selectedContactId,
      }),
    }
  )
);

// =============================================================================
// SEED MOCK DATA
// =============================================================================

// Flag to prevent concurrent seeding
let isSeeding = false;

export async function seedHubMockData() {
  const store = useHubStore.getState();

  // Prevent concurrent seeding and double-seeding
  if (isSeeding || store.conversations.length > 0) return;
  isSeeding = true;

  try {

  // Create contacts
  const cnt1 = await store.createContact({
    displayName: 'Mario Rossi',
    handles: { email: 'mario.rossi@example.com', phone: '+39 333 1234567', whatsapp: '+39 333 1234567' },
    company: 'Studio Lumen',
    role: 'CEO',
    tags: ['cliente', 'importante'],
  });

  const cnt2 = await store.createContact({
    displayName: 'Elena Bianchi',
    handles: { email: 'elena.bianchi@megacorp.it', phone: '+39 339 9876543' },
    company: 'MegaCorp SpA',
    role: 'Marketing Director',
    tags: ['cliente'],
  });

  const cnt3 = await store.createContact({
    displayName: 'Luca Verdi',
    handles: { telegram: '@lucaverdi', email: 'luca@freelance.it' },
    role: 'Designer Freelance',
    tags: ['collaboratore'],
  });

  // Create conversations
  const cnv1 = await store.createConversation({
    channel: 'email',
    title: 'Re: Proposta sito web',
    contactId: cnt1,
    contactName: 'Mario Rossi',
    unread: 2,
  });

  const cnv2 = await store.createConversation({
    channel: 'whatsapp',
    title: 'Aggiornamento convention',
    contactId: cnt2,
    contactName: 'Elena Bianchi',
    unread: 1,
    projectId: 'prj-convention', // Linked to project
  });

  const cnv3 = await store.createConversation({
    channel: 'telegram',
    title: 'Materiali grafici',
    contactId: cnt3,
    contactName: 'Luca Verdi',
    unread: 0,
  });

  const cnv4 = await store.createConversation({
    channel: 'call',
    title: 'Chiamata con Mario',
    contactId: cnt1,
    contactName: 'Mario Rossi',
    unread: 0,
  });

  // Add messages to conversations
  await store.addMessage(cnv1, {
    text: 'Buongiorno, ho visionato la proposta e ho alcune domande.',
    direction: 'in',
  });
  await store.addMessage(cnv1, {
    text: 'Certo, sono a disposizione. Quali aspetti vorreste approfondire?',
    direction: 'out',
  });
  await store.addMessage(cnv1, {
    text: 'Principalmente sui tempi di consegna e sulla gestione del CMS.',
    direction: 'in',
  });

  await store.addMessage(cnv2, {
    text: 'Ciao! Il catering ha confermato per 150 persone.',
    direction: 'in',
  });
  await store.addMessage(cnv2, {
    text: 'Perfetto! Procedo con la conferma definitiva.',
    direction: 'out',
  });

  await store.addMessage(cnv3, {
    text: 'Ho finito le bozze per il logo. Te le mando stasera.',
    direction: 'in',
  });

  await store.addMessage(cnv4, {
    text: 'Chiamata di 15 minuti - discussione progetto sito',
    direction: 'in',
  });
  } finally {
    isSeeding = false;
  }
}
